import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, FormControlLabel, Paper, Slider, Stack, Switch,
  ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { collection, onSnapshot, query, where } from 'firebase/firestore';

import { db } from '../firebase';
import { deriveJourney, serviceDays } from '../live2/deriveJourney';
import type { JourneyDoc, JourneyStatus } from '../live2/types';
import { LINES, LINE_WIDTH, STATIONS, VIEWBOX, Station, lookupStation } from './network';

/**
 * Whole-network live map. Renders all S-Bahn lines as SVG paths with a Canvas
 * overlay animating train positions at 60 fps.
 *
 * Geometry and topology come from `network.ts`, generated by
 * `tools/generate_network.py` off real AFZ journey records — do not hand-edit
 * station coordinates here.
 *
 * Animation pipeline (per line, same as the original S2 map):
 *   1. sample each line's SVG path with getPointAtLength to build
 *      stationName → arc-length
 *   2. per frame, interpolate s between the train's from/to stations
 *   3. getPointAtLength(s) → SVG coords → canvas pixels → rotated marker
 *
 * Currently driven by simulated trains. See CLAUDE.md for the Firestore wiring.
 */

const LINE_IDS = Object.keys(LINES);

const TRAVEL_V_MS = 3 * 60 * 1000; // 3 virtual minutes per leg
const DWELL_V_MS = 20 * 1000;      // 20 virtual seconds at a station

interface TrainState {
  id: string;
  line: string;
  fromIdx: number;
  toIdx: number;
  dir: 1 | -1;
  depMs: number;
  arrMs: number;
  dwelling: boolean;
  dwellUntilMs: number;
}

/**
 * One journey reduced to what the map needs: an ordered list of
 * (index into the line's station sequence, timestamp) waypoints.
 *
 * Unlike the legacy `lines/*&#47;routes` feed — which only ever carried the
 * current stop and its successor — `live_journeys` holds every stop of the
 * route, so a train can be drawn along its whole run.
 */
interface MapTrain {
  id: string;
  line: string;
  label: string;
  /** Station index on `LINES[line].stations`, ascending in time. */
  idx: number[];
  /** Wall-clock ms for each waypoint; strictly increasing, same length as idx. */
  ms: number[];
  delaySec: number | null;
  status: JourneyStatus;
}

type Mode = 'live' | 'replay' | 'sim';

/** EVA code → station, for placing a journey's stops on the map. */
const stationByEva: Record<string, Station> = {};
STATIONS.forEach((s) => {
  (s.allEva && s.allEva.length ? s.allEva : [s.eva]).forEach((e) => {
    if (e) stationByEva[e] = s;
  });
});

/** Station index on each line, so a stop can be located without a scan. */
const idxOnLine: Record<string, Record<string, number>> = {};
Object.keys(LINES).forEach((line) => {
  const m: Record<string, number> = {};
  LINES[line].stations.forEach((n, i) => { m[n] = i; });
  idxOnLine[line] = m;
});

/**
 * `live_journeys` document → an animatable train.
 *
 * The document is run through `deriveJourney` — the same derivation the /live2
 * board uses — so status, phases and the observed/scheduled precedence stay in
 * one place rather than being re-implemented here.
 *
 * A stop contributes a waypoint when it can be placed on this line's drawn path
 * and carries a time. Stops that can't be placed (a branch this line isn't drawn
 * through) are skipped rather than failing the journey. Returns null when fewer
 * than two waypoints survive, or the line isn't on the map.
 */
function journeyToTrain(id: string, doc: JourneyDoc, nowMs: number): MapTrain | null {
  const dj = deriveJourney(doc, null, nowMs);
  const line = dj.lineCode;
  const onLine = idxOnLine[line];
  if (!onLine) return null;

  const idx: number[] = [];
  const ms: number[] = [];
  for (const st of dj.stops) {
    if (st.cancelled) continue;
    const station = st.eva ? stationByEva[st.eva] : lookupStation(st.name);
    if (!station) continue;
    const i = onLine[station.name];
    if (i === undefined) continue;

    // Observed beats scheduled; departure beats arrival — a train leaves the
    // stop it is drawn at, so the departure clock governs the leg that follows.
    const t = st.departure.obsMs ?? st.departure.schedMs
           ?? st.arrival.obsMs ?? st.arrival.schedMs;
    if (t == null) continue;

    // Keep the series strictly increasing: a repeated station (the Stammstrecke
    // is shared) or a non-monotonic time would break the segment search.
    if (ms.length && (t <= ms[ms.length - 1] || i === idx[idx.length - 1])) continue;
    idx.push(i);
    ms.push(t);
  }
  if (idx.length < 2) return null;

  const label = `${line} ${dj.routeId ?? id.slice(-4)}`;
  return { id, line, label, idx, ms, delaySec: dj.delaySec, status: dj.status };
}

const stationByName: Record<string, Station> = {};
STATIONS.forEach((s) => { stationByName[s.name] = s; });

/** Stations served by more than one line, plus every line's termini. */
const majorStations = (() => {
  const count: Record<string, number> = {};
  const major = new Set<string>();
  LINE_IDS.forEach((id) => {
    const sts = LINES[id].stations;
    sts.forEach((n) => { count[n] = (count[n] ?? 0) + 1; });
    major.add(sts[0]);
    major.add(sts[sts.length - 1]);
  });
  Object.entries(count).forEach(([n, c]) => { if (c > 1) major.add(n); });
  return major;
})();

function clamp01(x: number) { return Math.max(0, Math.min(1, x)); }

/** Simulated trains: two per line per direction, staggered along the route. */
function makeFakeTrains(vNow: number): TrainState[] {
  const out: TrainState[] = [];
  LINE_IDS.forEach((line) => {
    const n = LINES[line].stations.length;
    if (n < 2) return;
    const starts = [Math.floor(n * 0.1), Math.floor(n * 0.55)];
    starts.forEach((f, i) => {
      out.push({
        id: `${line}-f${i}`, line, fromIdx: f, toIdx: Math.min(n - 1, f + 1), dir: 1,
        depMs: vNow - TRAVEL_V_MS * (0.3 + 0.2 * i),
        arrMs: vNow + TRAVEL_V_MS * (0.7 - 0.2 * i),
        dwelling: false, dwellUntilMs: 0,
      });
      const b = n - 1 - f;
      out.push({
        id: `${line}-b${i}`, line, fromIdx: b, toIdx: Math.max(0, b - 1), dir: -1,
        depMs: vNow - TRAVEL_V_MS * (0.6 - 0.2 * i),
        arrMs: vNow + TRAVEL_V_MS * (0.4 + 0.2 * i),
        dwelling: false, dwellUntilMs: 0,
      });
    });
  });
  return out;
}

const NetworkMap: React.FC = () => {
  const navigate = useNavigate();

  const pathRefs = useRef<Record<string, SVGPathElement | null>>({});
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // line → station name → arc-length along that line's path
  const distByLine = useRef<Record<string, Record<string, number>>>({});
  const trainsRef = useRef<TrainState[]>([]);
  const rafRef = useRef<number>(0);

  // virtual clock
  const speedRef = useRef(10);
  const anchorRealRef = useRef(Date.now());
  const anchorVirtualRef = useRef(Date.now());

  const visibleRef = useRef<Set<string>>(new Set(LINE_IDS));
  const [visible, setVisible] = useState<Set<string>>(new Set(LINE_IDS));
  const [speedDisplay, setSpeedDisplay] = useState(10);
  const [showLabels, setShowLabels] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Firestore-driven trains, from the single `live_journeys` listener.
  const liveTrainsRef = useRef<MapTrain[]>([]);
  const modeRef = useRef<Mode>('live');
  const [mode, setMode] = useState<Mode>('live');
  const [feed, setFeed] = useState<{ docs: number; trains: number; running: number }>(
    { docs: 0, trains: 0, running: 0 });

  function vNowMs() {
    return anchorVirtualRef.current + (Date.now() - anchorRealRef.current) * speedRef.current;
  }

  // ── Build the per-line arc-length maps once the SVG paths are in the DOM ──
  useEffect(() => {
    const SAMPLES = 1400;
    LINE_IDS.forEach((line) => {
      const path = pathRefs.current[line];
      if (!path) return;
      const L = path.getTotalLength();
      const byName: Record<string, number> = {};
      // Walk the path once, keeping the closest sample per station: O(samples)
      // per line instead of O(samples × stations).
      const targets = LINES[line].stations.map((n) => stationByName[n]).filter(Boolean);
      const best = targets.map(() => ({ s: 0, d2: Infinity }));
      for (let i = 0; i <= SAMPLES; i++) {
        const s = (i / SAMPLES) * L;
        const p = path.getPointAtLength(s);
        targets.forEach((st, k) => {
          const dx = p.x - st.x;
          const dy = p.y - st.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < best[k].d2) { best[k].d2 = d2; best[k].s = s; }
        });
      }
      targets.forEach((st, k) => { byName[st.name] = best[k].s; });
      distByLine.current[line] = byName;
    });
    trainsRef.current = makeFakeTrains(vNowMs());
    setInitialized(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Firestore: ONE listener on live_journeys, scoped to the service days ──
  // Same scoping as Sidebar2, and for the same reason: RIS:Journeys carries
  // timetable data ~48 h ahead and nothing deletes old documents, so an
  // unscoped listener would download the whole collection. `date_cal` is an
  // indexed top-level string, so this needs no composite index.
  //
  // No live_journey_occupancy listener — that collection is ~150 documents at
  // up to 48 KB, which is not worth paying for a board-wide view. Occupancy is
  // read per selected journey in LiveView2.
  useEffect(() => {
    const q = query(collection(db, 'live_journeys'),
                    where('date_cal', 'in', serviceDays(Date.now())));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const trains = snap.docs
          .map((d) => journeyToTrain(d.id, d.data() as JourneyDoc, now))
          .filter((t): t is MapTrain => t !== null);
        liveTrainsRef.current = trains;
        const running = trains.filter(
          (t) => now >= t.ms[0] && now <= t.ms[t.ms.length - 1]).length;
        setFeed({ docs: snap.size, trains: trains.length, running });
      },
      (err) => console.error('live_journeys onSnapshot error', err),
    );
    return () => unsub();
  }, []);

  // ── Keep canvas backing store in sync with its container ──────────────────
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    const ctx = canvas.getContext('2d')!;
    function resize() {
      const r = container!.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas!.width = Math.round(r.width * dpr);
      canvas!.height = Math.round(r.height * dpr);
      canvas!.style.width = `${r.width}px`;
      canvas!.style.height = `${r.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();
    return () => ro.disconnect();
  }, []);

  // ── Animation loop ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialized) return;
    const canvas = canvasRef.current!;
    const container = containerRef.current!;
    const ctx = canvas.getContext('2d')!;

    /**
     * SVG user space → canvas pixels, matching `preserveAspectRatio="xMidYMid
     * meet"`: one uniform scale plus the letterbox offset. It must mirror the
     * SVG's own fit exactly — a mismatch slides every train off its line.
     */
    function fit() {
      const r = container.getBoundingClientRect();
      const k = Math.min(r.width / VIEWBOX.w, r.height / VIEWBOX.h);
      return { k, ox: (r.width - VIEWBOX.w * k) / 2, oy: (r.height - VIEWBOX.h * k) / 2 };
    }

    function svgToCanvas(pt: { x: number; y: number }, f = fit()) {
      return { x: pt.x * f.k + f.ox, y: pt.y * f.k + f.oy };
    }

    /**
     * Train marker: rounded rear, wedge nose with a rounded tip, pointing the
     * way it travels — proportions taken from `docs/design-refs/train-icon-ref.svg`
     * (3.2:1, nose ≈ 15 % of the length).
     */
    function drawTrain(line: string, s: number) {
      const path = pathRefs.current[line];
      if (!path) return;
      const f = fit();
      const totalL = path.getTotalLength();
      const p = svgToCanvas(path.getPointAtLength(Math.max(0, Math.min(totalL, s))), f);
      const p2 = svgToCanvas(path.getPointAtLength(Math.min(totalL, s + 8)), f);
      const ang = Math.atan2(p2.y - p.y, p2.x - p.x);

      // Markers track the fit so they stay proportional in a small tile without
      // shrinking to nothing or swamping a large one.
      const m = Math.max(0.62, Math.min(1.3, f.k / 0.47));
      const w = 21 * m;
      const h = 6.6 * m;
      const nose = w * 0.15;      // wedge length at the front
      const tipR = h * 0.28;      // rounded point
      const rearR = h * 0.42;     // rear corner radius
      const xF = w / 2;
      const xB = -w / 2;
      const xN = xF - nose;       // where the wedge starts
      const yT = -h / 2;
      const yB = h / 2;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);
      ctx.fillStyle = LINES[line].color;
      // surface ring so overlapping markers stay separable
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.6 * m;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(xB + rearR, yT);
      ctx.lineTo(xN, yT);
      ctx.arcTo(xF, 0, xN, yB, tipR);   // nose: diagonal → rounded tip → diagonal
      ctx.lineTo(xN, yB);
      ctx.lineTo(xB + rearR, yB);
      ctx.arcTo(xB, yB, xB, yT, rearR);
      ctx.arcTo(xB, yT, xF, yT, rearR);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    function frame() {
      const r = container.getBoundingClientRect();
      ctx.clearRect(0, 0, r.width, r.height);
      const vNow = vNowMs();

      // Live/replay: each journey carries every stop, so a train is drawn on
      // whichever leg it is running at `vNow` and disappears once the run is
      // over — no parking at a last-known stop.
      if (modeRef.current !== 'sim') {
        liveTrainsRef.current.forEach((t) => {
          if (!visibleRef.current.has(t.line)) return;
          const last = t.ms.length - 1;
          if (vNow < t.ms[0] || vNow > t.ms[last]) return;

          // Locate the current leg. Journeys are ~15 stops, so a linear scan is
          // cheaper than the bookkeeping a binary search would need per frame.
          let k = 0;
          while (k < last - 1 && t.ms[k + 1] < vNow) k += 1;

          const seq = LINES[t.line].stations;
          const dist = distByLine.current[t.line] ?? {};
          const s0 = dist[seq[t.idx[k]]];
          const s1 = dist[seq[t.idx[k + 1]]];
          if (s0 === undefined || s1 === undefined) return;
          const span = t.ms[k + 1] - t.ms[k];
          const prog = span > 0 ? clamp01((vNow - t.ms[k]) / span) : 0;
          drawTrain(t.line, s0 + (s1 - s0) * prog);
        });
        rafRef.current = requestAnimationFrame(frame);
        return;
      }

      trainsRef.current.forEach((t) => {
        if (!visibleRef.current.has(t.line)) return;
        const seq = LINES[t.line].stations;
        const dist = distByLine.current[t.line] ?? {};

        // advance the little state machine: dwell → run → dwell …
        if (t.dwelling) {
          if (vNow >= t.dwellUntilMs) {
            const next = t.toIdx + t.dir;
            if (next < 0 || next >= seq.length) {
              t.dir = (t.dir === 1 ? -1 : 1) as 1 | -1;   // turn around at the terminus
            }
            t.fromIdx = t.toIdx;
            t.toIdx = Math.max(0, Math.min(seq.length - 1, t.fromIdx + t.dir));
            t.depMs = vNow;
            t.arrMs = vNow + TRAVEL_V_MS;
            t.dwelling = false;
          }
        } else if (vNow >= t.arrMs) {
          t.dwelling = true;
          t.dwellUntilMs = vNow + DWELL_V_MS;
        }

        const s0 = dist[seq[t.fromIdx]];
        const s1 = dist[seq[t.toIdx]];
        if (s0 === undefined || s1 === undefined) return;
        const prog = t.dwelling ? 1 : clamp01((vNow - t.depMs) / (t.arrMs - t.depMs));
        drawTrain(t.line, s0 + (s1 - s0) * prog);
      });

      rafRef.current = requestAnimationFrame(frame);
    }

    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [initialized]);

  function handleSpeedChange(_e: Event, value: number | number[]) {
    const v = Array.isArray(value) ? value[0] : value;
    anchorVirtualRef.current = vNowMs();   // anchor so the clock doesn't jump
    anchorRealRef.current = Date.now();
    speedRef.current = v;
    setSpeedDisplay(v);
  }

  /**
   * Re-anchor the virtual clock for the target mode, then switch.
   *  live   — clock tracks the wall clock at 1×, so positions are real.
   *  replay — clock starts at the earliest departure in the loaded batch and
   *           runs forward at the slider speed.
   *  sim    — clock starts now; fake trains carry their own schedule.
   */
  function switchMode(next: Mode) {
    anchorRealRef.current = Date.now();
    if (next === 'replay') {
      // Rewind to the start of the service day's earliest run so the whole
      // day can be watched at speed.
      const earliest = liveTrainsRef.current.reduce(
        (mn, t) => Math.min(mn, t.ms[0]), Number.POSITIVE_INFINITY);
      anchorVirtualRef.current = Number.isFinite(earliest) ? earliest : Date.now();
    } else {
      anchorVirtualRef.current = Date.now();
    }
    if (next === 'live') {
      speedRef.current = 1;
      setSpeedDisplay(1);
    }
    modeRef.current = next;
    setMode(next);
  }

  function toggleLine(id: string) {
    const next = new Set(visibleRef.current);
    if (next.has(id)) next.delete(id); else next.add(id);
    visibleRef.current = next;
    setVisible(next);
  }

  return (
    /* One screen, no page scroll: a fixed-height column whose only flexible row
       is the map tile. The map used to be aspect-locked and taller than the
       viewport, which forced a page scrollbar — and the wheel-zoom that sat on
       the tile then swallowed every scroll gesture. */
    <Box sx={{
      height: '100vh', overflow: 'hidden', p: 2,
      display: 'flex', flexDirection: 'column', gap: 1.25,
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/home')}>
          Zurück
        </Button>
        <Typography variant="h6" sx={{ mr: 1 }}>Streckennetz — Stuttgart S-Bahn</Typography>

        <ToggleButtonGroup
          size="small" exclusive value={mode}
          onChange={(_e, v) => { if (v) switchMode(v); }}
        >
          <ToggleButton value="live">Live</ToggleButton>
          <ToggleButton value="replay">Tagesverlauf</ToggleButton>
          <ToggleButton value="sim">Simulation</ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary">
          {mode === 'sim'
            ? 'Simulierte Fahrten'
            : `${feed.trains} Fahrten · ${feed.running} unterwegs`}
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        {/* Live positions must track the wall clock — accelerating would put
            trains ahead of where they actually are. */}
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Zeitraffer ×{speedDisplay}
        </Typography>
        <Slider
          min={1} max={60} value={speedDisplay} onChange={handleSpeedChange}
          valueLabelDisplay="auto" size="small" disabled={mode === 'live'}
          sx={{ width: 140 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showLabels}
                           onChange={(e) => setShowLabels(e.target.checked)} />}
          label={<Typography variant="body2">Stationsnamen</Typography>}
        />
      </Stack>

      {mode !== 'sim' && feed.docs > 0 && feed.trains === 0 && (
        <Alert severity="warning" sx={{ py: 0 }}>
          {feed.docs} Fahrtdokumente geladen, aber keine davon lässt sich auf dem
          Netz platzieren — vermutlich Linien oder Haltestellen, die die Karte
          noch nicht kennt.
        </Alert>
      )}
      {mode !== 'sim' && feed.docs === 0 && (
        <Alert severity="info" sx={{ py: 0 }}>
          Keine Fahrten aus <code>live_journeys</code> geladen — Anmeldung und
          Firestore-Regeln prüfen.
        </Alert>
      )}

      {/* Line badges double as the legend, so identity is never colour-alone. */}
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
        {LINE_IDS.map((id) => (
          <Chip
            key={id}
            size="small"
            label={id}
            onClick={() => toggleLine(id)}
            variant={visible.has(id) ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 700,
              color: visible.has(id) ? '#fff' : LINES[id].color,
              bgcolor: visible.has(id) ? LINES[id].color : 'transparent',
              borderColor: LINES[id].color,
              '&:hover': { bgcolor: visible.has(id) ? LINES[id].color : 'transparent' },
            }}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {STATIONS.length} Stationen · {LINE_IDS.length} Linien ·
          {mode === 'sim'
            ? ' simulierte Fahrten'
            : ' Fahrten aus live_journeys (vollständige Route)'}
        </Typography>
      </Stack>

      {/* The tile. `minHeight: 0` lets it shrink inside the flex column instead
          of overflowing it; the SVG letterboxes itself to fit. */}
      <Paper
        ref={containerRef}
        elevation={1}
        sx={{
          position: 'relative', flexGrow: 1, minHeight: 0, width: '100%',
          overflow: 'hidden', bgcolor: 'background.paper',
        }}
      >
        <svg
          viewBox={`0 0 ${VIEWBOX.w} ${VIEWBOX.h}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
        >
          {LINE_IDS.map((id) => (
            <path
              key={id}
              ref={(el) => { pathRefs.current[id] = el; }}
              d={LINES[id].d}
              fill="none"
              stroke={LINES[id].color}
              strokeDasharray={LINES[id].dash || undefined}
              strokeWidth={LINE_WIDTH}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={visible.has(id) ? 1 : 0.08}
            />
          ))}

          {STATIONS.map((s) => {
            const major = majorStations.has(s.name);
            return (
              <g key={s.eva || s.name}>
                {/* Sized off the line, not in absolute pixels: the reference
                    draws no station dots at all, so they have to read as ticks
                    ON the track rather than blobs beside it. */}
                <circle
                  cx={s.x} cy={s.y}
                  r={LINE_WIDTH * (major ? 0.78 : 0.5)}
                  fill="#ffffff" stroke="#374151"
                  strokeWidth={LINE_WIDTH * (major ? 0.27 : 0.2)}
                />
                {showLabels && major && (
                  <text
                    x={s.x + LINE_WIDTH * 1.2} y={s.y + 5}
                    fontSize={19} fontWeight={600} fill="#111827"
                    style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 4 }}
                  >
                    {s.name.replace('Stuttgart ', '').replace('Stuttgart-', '')}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
        />
      </Paper>
    </Box>
  );
};

export default NetworkMap;
